import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import Tooltip from "../Tooltip/Tooltip";
import DateToolTip from "../DateToolTip/DateToolTip";
import useHover from "../../hooks/useHover";
import useFileManagementData from "./useFileManagementData";
import styles from "./styles.module.css";
import Pagination from "../Pagination";
import StaticTable from "../StaticTable/StaticTable";
import DateRangePicker from "../DateRangePicker";
import CustomSelect from "../CustomSelect";
import useNotification from "../../hooks/useNotification";
import useAuth from "../../hooks/useAuth";
import { PageIdentityManagement } from "../../utils/controls";
import useCustomRouting from "../../hooks/useCustomRouting";
import { UploadForm } from "./UploadForm";
import DownloadCell from "./DownloadCell";

const toDateFromYmd = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const addMonths = (date, months) => {
  if (!date) return null;
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

const getMaxDateFromStart = (start) => {
  if (!start) return null;

  const maxByRange = addMonths(start, 3);
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  return new Date(Math.min(maxByRange.getTime(), today.getTime()));
};

const STATUS_CONFIG = {
  SUCCESS: { label: "Success", className: styles.statusSuccess },
  PARTIAL_SUCCESS: {
    label: "Partial Success",
    className: styles.statusPartialSuccess,
  },
  FAILED: { label: "Failed", className: styles.statusFailed },
  PROCESSING: { label: "Processing", className: styles.statusDefault },
  STARTED: { label: "Started", className: styles.statusDefault },
  VALIDATED: { label: "Validated", className: styles.statusDefault },
};

const DOWNLOAD_DATA_FILE_TYPE = "data";
const DOWNLOAD_ISSUES_FILE_TYPE = "issues";

const getColumns = ({
  intl,
  handleMouseEnter,
  handleMouseLeave,
  hoveredData,
  ishovered,
}) => [
  {
    header: intl.formatMessage({
      id: "label.file-name",
      defaultMessage: "File Name",
    }),
    accessorKey: "fileName",
  },
  {
    header: intl.formatMessage({
      id: "label.functionality",
      defaultMessage: "Functionality",
    }),
    accessorKey: "functionality",
  },
  {
    header: intl.formatMessage({
      id: "label.total-records",
      defaultMessage: "Total Records",
    }),
    accessorKey: "totalRecords",
  },
  {
    header: intl.formatMessage({
      id: "label.successful-records",
      defaultMessage: "Successful Records",
    }),
    accessorKey: "successfulRecords",
  },
  {
    header: intl.formatMessage({
      id: "label.failed-records",
      defaultMessage: "Failed Records",
    }),
    accessorKey: "failedRecords",
  },
  {
    header: intl.formatMessage({
      id: "label.issues",
      defaultMessage: "Issues",
    }),
    accessorKey: "issues",
    cell: (info) => {
      const row = info.row.original;
      const issuesCount = Number(row?.issues) || 0;
      if (issuesCount <= 0) {
        return <span>0</span>;
      }
      return (
        <DownloadCell
          intl={intl}
          batchId={row?.batchId}
          fileName={row?.fileName}
          downloadType={DOWNLOAD_ISSUES_FILE_TYPE}
          label={issuesCount}
        />
      );
    },
  },
  {
    header: intl.formatMessage({
      id: "label.status",
      defaultMessage: "Status",
    }),
    accessorKey: "status",
    cell: (info) => {
      const value = info.getValue();
      const config = STATUS_CONFIG[value] ?? {
        label: value,
        className: styles.statusDefault,
      };
      return (
        <span className={`${styles.status} ${config.className}`}>
          {config.label}
        </span>
      );
    },
  },
  {
    header: intl.formatMessage({
      id: "label.uploaded-by",
      defaultMessage: "Uploaded by",
    }),
    accessorKey: "uploadedBy",
  },
  {
    header: intl.formatMessage({
      id: "label.source-system",
      defaultMessage: "Source System",
    }),
    accessorKey: "sourceSystem",
  },
  {
    header: () => (
      <Tooltip position="right" text="Date format: DD.MM.YYYY HH:mm">
        <span className="pe-1">
          {intl.formatMessage({
            id: "label.uploaded-date",
            defaultMessage: "Uploaded Date",
          })}
        </span>
      </Tooltip>
    ),
    accessorKey: "uploadedDateUtc",
    sortType: (rowA, rowB) =>
      new Date(rowA.original.uploadedDateUtc) -
      new Date(rowB.original.uploadedDateUtc),
    cell: (info) => {
      const value = info.getValue();
      if (!value || !moment(value).isValid()) return "Invalid Date";
      return (
        <button
          onMouseEnter={() => handleMouseEnter(value)}
          onMouseLeave={handleMouseLeave}
          className={`d-flex align-items-center ${styles.dateButton}`}
        >
          <span className="pe-1">
            {moment.utc(value).local().format("DD.MM.YYYY HH:mm")}
          </span>
          <DateToolTip ishovered={ishovered} hoveredData={hoveredData} />
        </button>
      );
    },
  },
  {
    header: intl.formatMessage({
      id: "label.data-file",
      defaultMessage: "Data File",
    }),
    accessorKey: "downloadLink",
    cell: (info) => {
      const row = info.row.original;
      return (
        <DownloadCell
          intl={intl}
          batchId={row?.batchId}
          fileName={row?.fileName}
          downloadType={DOWNLOAD_DATA_FILE_TYPE}
        />
      );
    },
  },
];

const FileManagement = () => {
  const intl = useIntl();
  const { redirectToPath } = useCustomRouting();
  const { isAdmin } = useAuth();
  const { handleMouseEnter, handleMouseLeave, hoveredData, ishovered } =
    useHover();

  useEffect(() => {
    if (!isAdmin()) {
      redirectToPath(PageIdentityManagement);
    }
  }, [isAdmin, redirectToPath]);

  const {
    data,
    loading,
    page,
    pageSize,
    totalCount,
    filters,
    functionalityOptions,
    updateFunctionality,
    updateFromDate,
    updateToDate,
    handleFirstPageChange,
    handlePrevPageChange,
    handleNextPageChange,
    handleLastPageChange,
    setPageSize,
    resetFilters,
    resetDateFilters,
    refetch
  } = useFileManagementData();

  const columns = useMemo(
    () =>
      getColumns({
        intl,
        handleMouseEnter,
        handleMouseLeave,
        hoveredData,
        ishovered,
      }),
    [intl, handleMouseEnter, handleMouseLeave, hoveredData, ishovered],
  );

  return (
    <>
      <h2 className="fs-3 fw-normal py-2">
        <FormattedMessage
          id="file-management.title"
          defaultMessage="File Management"
        />
      </h2>
      <div className={styles.wrapper}>
        <FileManagementFilters
          filters={filters}
          functionalityOptions={functionalityOptions}
          updateFunctionality={updateFunctionality}
          updateFromDate={updateFromDate}
          updateToDate={updateToDate}
          resetFilters={resetFilters}
          resetDateFilters={resetDateFilters}
          refetch={refetch}
        />
        <StaticTable
          loading={loading}
          customClass={styles.tableWrapper}
          data={data}
          columns={columns}
          messageId="file-management.no-records"
          defaultErrorMessage="No Records Found"
          clickableRow={false}
        />
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        pageSizeOptions={[10, 25, 50]}
        onFirstPage={handleFirstPageChange}
        onPrevPage={handlePrevPageChange}
        onNextPage={handleNextPageChange}
        onLastPage={handleLastPageChange}
        onPageSizeChange={setPageSize}
      />
    </>
  );
};

// ─── FileManagementFilters ────────────────────────────────────────────────────

const FileManagementFilters = ({
  filters,
  functionalityOptions,
  updateFunctionality,
  updateFromDate,
  updateToDate,
  resetFilters,
  resetDateFilters,
  refetch
}) => {
  const intl = useIntl();
  const { errorToast } = useNotification();

  const selectedFunctionality =
    functionalityOptions.find(
      (opt) => opt.value === filters.functionalityName,
    ) || functionalityOptions[0];

  const handleDateRangeChange = (start, end) => {
    updateFromDate(
      start
        ? `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`
        : "",
    );
    updateToDate(
      end
        ? `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`
        : "",
    );
  };

  const isWithinThreeMonths = (start, end) => {
    if (!start || !end) return false;

    const max = new Date(start);
    max.setMonth(max.getMonth() + 3);
    if (end > max) {
      errorToast(
        "Date range cannot exceed 3 months. Please choose a shorter range.",
      );
      return false;
    }

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (end > today || start > today) {
      errorToast("Selected date cannot be in the future.");
      return false;
    }

    return true;
  };

  return (
    <div className={styles.filterWrapper}>
      <div className={styles.field}>
        <label className={`form-label ${styles.label}`}>
          {intl.formatMessage({
            id: "file-management.functionality",
            defaultMessage: "Functionality",
          })}
        </label>
        <CustomSelect
          className={styles.dropdown}
          options={functionalityOptions}
          value={selectedFunctionality}
          onChange={(option) => updateFunctionality(option?.value || "All")}
          variant="select-search"
        />
      </div>

      <div className={styles.field}>
        <label className={`form-label ${styles.label}`}>
          {intl.formatMessage({
            id: "file-management.uploaded-date",
            defaultMessage: "Uploaded Date",
          })}
        </label>
        <DateRangePicker
          startDate={toDateFromYmd(filters.startDate)}
          endDate={toDateFromYmd(filters.endDate)}
          getMaxDate={getMaxDateFromStart}
          onApply={handleDateRangeChange}
          isRangeAllowed={isWithinThreeMonths}
          onInvalidRange={resetDateFilters}
        />
      </div>

      <button className="btn-secondary" onClick={resetFilters}>
        <FormattedMessage id="file-management.reset" defaultMessage="Reset" />
      </button>

      <UploadForm onUploadSuccess={refetch} />
    </div>
  );
};

FileManagementFilters.propTypes = {
  filters: PropTypes.shape({
    functionalityName: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  functionalityOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  updateFunctionality: PropTypes.func.isRequired,
  updateFromDate: PropTypes.func.isRequired,
  updateToDate: PropTypes.func.isRequired,
  resetFilters: PropTypes.func.isRequired,
  resetDateFilters: PropTypes.func.isRequired,
  refetch: PropTypes.func,
};

export default FileManagement;